import React, { useCallback } from 'react';
import { DataTableFilterOption, Operator } from './types';
import Input from '../../components/Input';
import { CancelIcon } from '../../components/icons';
import { nexyColors } from '../../theme';

interface DataTableAdvancedTitleFilterProps {
  selectedValues: Set<string>;
  setSelectedOptions: React.Dispatch<React.SetStateAction<DataTableFilterOption[]>>;
  setShouldFetch: (value: boolean) => void;
  selectedOperator: Operator;
  selectedOption: DataTableFilterOption;
  /**
   * Offered as one-click "+ value" adds under the field. Used by surfaces that pick from a
   * known vocabulary (channels, objectives) while still allowing free text — the faceted
   * list cannot serve them, because it can only render values present in `options` and would
   * make a saved out-of-vocabulary value invisible and unremovable.
   */
  suggestions?: string[];
}

const FilterValue = React.memo(({ val, onRemove, isLast }: { val: string; onRemove: () => void; isLast: boolean }) => (
  <>
    <span className="flex gap-1 rounded-md border border-neutral-200 bg-white px-2 py-1 text-xs text-neutral-600">
      <span className="max-w-56 truncate">{val}</span>
      {/* A real, named button rather than a bare <div onClick>: the control is otherwise
          unreachable by keyboard and unnameable by a screen reader. The name is the one
          the labels drawer's own editor used before this component replaced it. */}
      <button
        type="button"
        aria-label={`Remove keyword ${val}`}
        className="flex cursor-pointer items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
        onClick={onRemove}
      >
        <CancelIcon style={{ color: nexyColors.neutral300, height: 8 }} />
      </button>
    </span>
    {!isLast && <span className="self-center text-sm text-neutral-400">OR</span>}
  </>
));

FilterValue.displayName = 'FilterValue';

export function AdvancedTitleFilter({
  selectedValues,
  setSelectedOptions,
  setShouldFetch,
  selectedOperator,
  selectedOption,
  suggestions = [],
}: DataTableAdvancedTitleFilterProps) {
  const [inputValue, setInputValue] = React.useState('');

  // Never mutate `selectedValues`: it is memoised by the caller off `filterValues`, so an
  // in-place add/delete silently rewrites the caller's cached Set even when the caller
  // never accepts the commit.
  const writeValues = useCallback(
    (nextValues: string[]) => {
      setSelectedOptions((prev) =>
        prev.map((item) =>
          item.id === selectedOption.id
            ? {
                ...item,
                filterValues: nextValues,
                filterOperator: selectedOperator?.value,
              }
            : item,
        ),
      );
    },
    [setSelectedOptions, selectedOperator, selectedOption],
  );

  // Blank/whitespace input is a no-op, and so is an EXACT repeat. Matching is case-sensitive
  // server-side, so "dsa" and "DSA" reach different contents and are both kept.
  const addKeyword = useCallback(
    (raw: string) => {
      const keyword = raw.trim();
      if (!keyword || selectedValues.has(keyword)) return;

      writeValues([...Array.from(selectedValues), keyword]);
    },
    [selectedValues, writeValues],
  );

  // ENTER IS THE ONLY COMMIT, deliberately.
  //
  // A commit-on-blur was tried here, to stop a keyword the user typed but did not confirm
  // vanishing with the popover. It cannot serve that case: Radix dismisses the popover on
  // `pointerdown`, React flushes discrete events synchronously, so this input is unmounted
  // before any focusout reaches React — and an element removed while focused fires no blur
  // at all. Escape is the same story. So the close paths never saw it.
  //
  // What it did see was every focus move that keeps the popover OPEN, and there it was
  // actively wrong: `nik` — a typo on its way to being erased — was committed as a real
  // keyword on the rule the moment the user reached for the operator Select or a pill's ✕.
  // A commit that can only fire when it is unwanted is worse than no commit.
  const commitDraft = useCallback(() => {
    addKeyword(inputValue);
    setInputValue('');
  }, [addKeyword, inputValue]);

  const handleMainInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitDraft();
      }
    },
    [commitDraft],
  );

  const handleRemoveValue = useCallback(
    (valueToRemove: string) => {
      writeValues(Array.from(selectedValues).filter((existing) => existing !== valueToRemove));
    },
    [selectedValues, writeValues],
  );

  const unusedSuggestions = suggestions.filter((suggestion) => !selectedValues.has(suggestion));

  return (
    <div className="space-y-4">
      <Input
        autoFocus
        placeholder="Type here and press Enter to add..."
        // A placeholder stops being the accessible name the moment the field has content.
        aria-label="Add keyword"
        className="!focus:shadow-none !mt-2 h-8 w-full"
        color="dark"
        value={inputValue}
        onChange={(event) => {
          setInputValue(event.target.value);
          setShouldFetch(false);
        }}
        onKeyDown={handleMainInputKeyDown}
      />
      {selectedValues.size > 0 && (
        <div className="flex flex-wrap gap-2">
          {Array.from(selectedValues).map((val, index) => (
            <FilterValue
              key={val + index}
              val={val}
              onRemove={() => handleRemoveValue(val)}
              isLast={index === selectedValues.size - 1}
            />
          ))}
        </div>
      )}
      {unusedSuggestions.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {unusedSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => addKeyword(suggestion)}
              className="rounded-md border border-neutral-200 bg-white px-2 py-0.5 text-xs text-neutral-600 transition-colors hover:border-neutral-400 hover:text-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400"
            >
              {`+ ${suggestion}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
